import type {
  LocalStoragePendingReminderShillUpsellAttributionRecord,
  LocalStorageReminderShillUpsellAttributionContext,
} from "@/modules/local-storage/types";

const PENDING_ATTRIBUTION_STORAGE_KEY = "mem.reminder_shill.upsell_attribution_pending";
const PENDING_ATTRIBUTION_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * Accepts only the reminder-shill attribution shape this client can safely replay.
 */
const isReminderShillUpsellAttributionContext = (
  value: unknown
): value is LocalStorageReminderShillUpsellAttributionContext => {
  if (!value || typeof value !== "object") return false;

  const maybeContext = value as Partial<LocalStorageReminderShillUpsellAttributionContext>;

  return (
    typeof maybeContext.noteId === "string" &&
    typeof maybeContext.reminderShillId === "string" &&
    typeof maybeContext.generatedReminderText === "string" &&
    maybeContext.source === "editor_reminder_shill"
  );
};

/**
 * Restores a pending attribution record only while it is fresh and structurally valid.
 */
const parsePendingAttributionRecord = (
  rawValue: string
): LocalStoragePendingReminderShillUpsellAttributionRecord | null => {
  try {
    const parsed = JSON.parse(rawValue) as {
      context?: unknown;
      savedAtMs?: unknown;
    };

    if (
      !isReminderShillUpsellAttributionContext(parsed.context) ||
      typeof parsed.savedAtMs !== "number"
    ) {
      return null;
    }

    if (Date.now() - parsed.savedAtMs > PENDING_ATTRIBUTION_TTL_MS) {
      return null;
    }

    return {
      context: parsed.context,
      savedAtMs: parsed.savedAtMs,
    };
  } catch {
    return null;
  }
};

/**
 * Stores checkout attribution in session scope before checkout leaves the app.
 */
export const writePendingReminderShillUpsellAttribution = (
  context: LocalStorageReminderShillUpsellAttributionContext | null | undefined
): void => {
  if (!context) {
    clearPendingReminderShillUpsellAttribution();
    return;
  }

  try {
    const record: LocalStoragePendingReminderShillUpsellAttributionRecord = {
      context,
      savedAtMs: Date.now(),
    };

    window.sessionStorage.setItem(PENDING_ATTRIBUTION_STORAGE_KEY, JSON.stringify(record));
  } catch {
    // noop
  }
};

/**
 * Reads pending checkout attribution, removing stale or malformed records.
 */
export const readPendingReminderShillUpsellAttribution =
  (): LocalStorageReminderShillUpsellAttributionContext | null => {
    try {
      const rawValue = window.sessionStorage.getItem(PENDING_ATTRIBUTION_STORAGE_KEY);

      if (!rawValue) return null;

      const record = parsePendingAttributionRecord(rawValue);

      if (!record) {
        window.sessionStorage.removeItem(PENDING_ATTRIBUTION_STORAGE_KEY);
        return null;
      }

      return record.context;
    } catch {
      return null;
    }
  };

/**
 * Removes pending reminder-shill checkout attribution for canceled or failed flows.
 */
export const clearPendingReminderShillUpsellAttribution = (): void => {
  try {
    window.sessionStorage.removeItem(PENDING_ATTRIBUTION_STORAGE_KEY);
  } catch {
    // noop
  }
};
